import { Button } from '@/components/ui/button';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { EventType } from '@/api';  // Make sure this imports from where your enum is defined
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';


interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewScoreLogicDrawer({ open, setOpen }: Props) {
  const org = useStore($org);
  const eventTypes = useStore($eventTypes);
  const loading = useStore($loadingEventTypes);

  // If event types aren't loaded yet, trigger the load
  useEffect(() => {
    if (!eventTypes.length && org?.id) {
      console.log("Loading event types... again");
      loadScoreLogics(org.id);
    }
  }, [org?.id]);

  // Create form schema after we have event types
  const formSchema = z.object({
    Name: z.string().min(2, {
      message: 'Score Logic name must be at least 2 characters.',
    }),
    organizationId: z.string(),
    description: z.string().nullable().optional(),
    portoflioId: z.string(),
    accountReceivableId: z.string(),
    eventType: z.enum(
      Array.isArray(eventTypes) 
        ? eventTypes
            .map(et => et.value ?? '')
            .filter((value): value is string => value !== '') as [string, ...string[]]
        : ['Payment']  // Fallback if eventTypes isn't an array
    ),
    condition: z.string(),
    impactValue: z.number(),
    priority: z.number(),
    isActive: z.boolean(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      Name: 'Test Score Logic',
      organizationId: org?.id ?? '',
      description: null,
      portoflioId: '',
      accountReceivableId: '',
      eventType: Array.isArray(eventTypes) 
        ? (eventTypes[0]?.value ?? 'Payment')
        : 'Payment',
      condition: '',
      impactValue: 0,
      priority: 1,
      isActive: true,
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  async function onSubmit(values: z.infer<typeof formSchema>) {

    const result = await createScoreLogic(
      values.Name || '',
      org?.id || '',
      values.accountReceivableId || '',
      values.eventType as EventType,
      values.condition ?? '',
      values.impactValue ?? 0,
      values.priority ?? 1,
      values.isActive ?? true,  
    );
    if (!result?.succeeded) {
      toast('Error creating new score logic:', { // Updated error message
        description: result?.message,
        duration: 5000,
      });
    }

    setIsLoading(false);
    setOpen(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create new Score Logic</SheetTitle>
          <SheetDescription>
            Fill in the details for the new score logic.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-8">
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Score Logic Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Score Logic ABC" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a Score Logic name to help identify this file.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="accountReceivableId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Receivable</FormLabel>
                  <FormControl>
                    <Input placeholder="Account Receivable ABC" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the Account Receivable ID to link this score logic to an account receivable.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="eventType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Event Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(eventTypes) && eventTypes.map(type => (
                          <SelectItem 
                            key={type.value ?? ''} 
                            value={type.value ?? ''}
                          >
                            {type.value ?? ''}
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Description" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormDescription>
                    Enter a description for this score logic.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="impactValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Impact Value</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Impact Value" 
                      {...field}
                      onChange={e => field.onChange(Number(e.target.value))}
                      value={field.value ?? 0} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Priority" 
                      {...field}
                      onChange={e => field.onChange(Number(e.target.value))}
                      value={field.value ?? 1} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="condition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condition</FormLabel>
                  <FormControl>
                    <Input placeholder="Condition" {...field} value={field.value ?? ''} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Is Active</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}